
import React, { useState, useRef, useEffect } from 'react';
import { Bell, CheckCircle, Info, AlertTriangle, XCircle, BellOff, Check, Trash2 } from 'lucide-react';
import { Notification } from '../../types';

interface NotificationPanelProps {
    notifications: Notification[];
    onMarkAllRead: () => void;
    onClearAll: () => void;
    onNotificationClick?: (notification: Notification) => void;
}

export const NotificationPanel: React.FC<NotificationPanelProps> = ({ notifications, onMarkAllRead, onClearAll, onNotificationClick }) => {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);
    
    const normalizeTime = (iso?: string | null) => {
        if (!iso) return '';
        const d = new Date(iso);
        if (isNaN(d.getTime())) return '';
        return d.toLocaleString(undefined, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
        });
    };

    const normalized = notifications.map((n) => ({
        ...n,
        displayTime: normalizeTime(n.time),
    }));
    const unreadCount = normalized.filter(n => !n.read).length;

    const toggleOpen = () => setIsOpen(!isOpen);

    // Handle Click Outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const handleItemClick = (notif: Notification) => {
        if (onNotificationClick) {
            onNotificationClick(notif);
            setIsOpen(false);
        }
    };

    const getIcon = (type: string) => {
        switch(type) {
            case 'success': return <CheckCircle className="w-5 h-5 text-emerald-500" />;
            case 'warning': return <AlertTriangle className="w-5 h-5 text-amber-500" />;
            case 'error': return <XCircle className="w-5 h-5 text-red-500" />;
            default: return <Info className="w-5 h-5 text-teal-500" />;
        }
    };

    const getBgColor = (type: string) => {
        switch(type) {
            case 'success': return 'bg-emerald-50';
            case 'warning': return 'bg-amber-50';
            case 'error': return 'bg-red-50';
            default: return 'bg-teal-50';
        }
    };

    return (
        <div className="flex items-center gap-3" ref={dropdownRef}>
            {/* Label Section - Visible on larger screens */}
            <div 
                className="hidden lg:block text-right cursor-pointer group" 
                onClick={toggleOpen}
            >
                <h3 className="font-bold text-zinc-900 text-sm leading-tight group-hover:text-teal-600 transition-colors">Notifications</h3>
                <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider mt-0.5">
                    {unreadCount > 0 ? (
                        <span className="text-teal-500">You have {unreadCount} unread</span>
                    ) : (
                        'No new messages'
                    )}
                </p>
            </div>

            {/* Bell Button & Dropdown Container */}
            <div className="relative">
                <button
                    onClick={toggleOpen}
                    className={`
                        relative p-3 rounded-xl transition-all duration-200 active:scale-95 border
                        ${isOpen 
                            ? 'bg-teal-50 text-teal-600 border-teal-100 shadow-inner' 
                            : 'bg-white text-zinc-500 border-transparent hover:bg-zinc-50 hover:text-zinc-700'
                        }
                    `}
                >
                    <Bell className="w-5 h-5" />
                    {unreadCount > 0 && (
                        <span className="absolute top-2.5 right-2.5 flex h-2.5 w-2.5">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500 border-2 border-white animate-bounce"></span>
                        </span>
                    )}
                </button>

                {isOpen && (
                    <div className="absolute right-0 mt-4 w-80 sm:w-96 bg-white rounded-2xl shadow-xl shadow-zinc-200/50 border border-zinc-100 z-50 overflow-hidden animate-fade-in-up origin-top-right ring-1 ring-zinc-900/5">
                        
                        {/* Header */}
                        <div className="px-5 py-4 border-b border-zinc-100 flex justify-between items-center bg-white">
                            <div>
                                <h3 className="font-bold text-zinc-900 text-sm">Notifications</h3>
                                <p className="text-[10px] text-zinc-400 font-bold uppercase tracking-wider mt-0.5">
                                    Recent Activity
                                </p>
                            </div>
                            
                            <div className="flex items-center gap-1">
                                {unreadCount > 0 && (
                                    <button
                                        onClick={onMarkAllRead}
                                        className="flex items-center gap-1 text-[10px] font-bold text-teal-600 hover:text-teal-700 hover:bg-teal-50 px-2 py-1 rounded-lg transition-colors"
                                        title="Mark all as read"
                                    >
                                        <Check className="w-3 h-3" />
                                        Read
                                    </button>
                                )}
                                {notifications.length > 0 && (
                                    <button
                                        onClick={onClearAll}
                                        className="flex items-center gap-1 text-[10px] font-bold text-zinc-400 hover:text-red-600 hover:bg-red-50 px-2 py-1 rounded-lg transition-colors"
                                        title="Clear all notifications"
                                    >
                                        <Trash2 className="w-3 h-3" />
                                        Clear
                                    </button>
                                )}
                            </div>
                        </div>
                        
                        {/* List */}
                        <div className="max-h-[400px] overflow-y-auto custom-scrollbar bg-zinc-50/30">
                            {notifications.length === 0 ? (
                                <div className="py-12 px-8 text-center flex flex-col items-center justify-center">
                                    <div className="w-16 h-16 bg-zinc-50 rounded-full flex items-center justify-center mb-4 text-zinc-300">
                                        <BellOff className="w-8 h-8" />
                                    </div>
                                    <p className="text-zinc-900 font-bold text-sm">All caught up!</p>
                                    <p className="text-zinc-400 text-xs mt-1">No new notifications to display.</p>
                                </div>
                            ) : (
                                normalized.map(notif => (
                                    <div 
                                        key={notif.id} 
                                        onClick={() => handleItemClick(notif)}
                                        className={`
                                            group relative p-4 border-b border-zinc-100 last:border-0 transition-all 
                                            ${!notif.read ? 'bg-white' : 'bg-zinc-50/30 opacity-80 hover:opacity-100'}
                                            ${notif.targetView ? 'cursor-pointer hover:bg-teal-50/50' : ''}
                                        `}
                                    >
                                        {!notif.read && (
                                            <div className="absolute left-0 top-6 w-1 h-8 bg-teal-500 rounded-r-full"></div>
                                        )}
                                        
                                        <div className="flex gap-4 pl-2">
                                            {/* Icon Box */}
                                            <div className={`mt-1 shrink-0 w-10 h-10 rounded-xl flex items-center justify-center shadow-sm border border-black/5 ${getBgColor(notif.type)}`}>
                                                {getIcon(notif.type)}
                                            </div>
                                            
                                            {/* Content */}
                                            <div className="flex-1 min-w-0">
                                                <div className="flex justify-between items-start mb-1 gap-2">
                                                    <h4 className={`text-sm leading-snug ${!notif.read ? 'font-bold text-zinc-900' : 'font-semibold text-zinc-600'}`}>
                                                        {notif.title}
                                                    </h4>
                                                    <span className="shrink-0 text-[10px] font-bold text-zinc-400 bg-zinc-100 px-1.5 py-0.5 rounded whitespace-nowrap">
                                                        {notif.displayTime || '—'}
                                                    </span>
                                                </div>
                                                <p className="text-xs text-zinc-500 leading-relaxed line-clamp-2">
                                                    {notif.message}
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};
